import { useCallback, useEffect, useMemo, useState } from 'react'
import { Loader2, Inbox, Filter } from 'lucide-react'
import { apiGet, cachedGet } from '../../lib/api.js'
import { apiErrorText } from '../../lib/apiError.js'
import { usePersistedState } from '../../lib/persist.js'
import { fullName, indexBranches } from '../../lib/employee.js'
import { nameInitial } from '../../lib/text.js'
import { EmployeeTrigger } from '../../components/employee/EmployeeDrawer.jsx'
import { leaveTypeMeta } from '../../lib/timetable.js'
import { statusLabel, statusTone, fmtRange, applyWarning, PERSONAL_REVIEW_DAYS, LEAVE_PERSONAL } from '../../lib/leaveRequest.js'
import LeaveRequestDrawer from '../../components/employee/LeaveRequestDrawer.jsx'
import EmployeePicker from '../../components/employee/EmployeePicker.jsx'
import { useEmployeeFilter } from '../../components/employee/EmployeeFilter.jsx'

const SELECT = 'rounded-xl border border-slate-200 bg-white px-3 py-2 text-[13.5px] text-slate-700 outline-none focus:border-brand-400 focus:ring-4 focus:ring-brand-500/10'

const FILTERS = [
  { key: 'pending', label: 'รอพิจารณา' },
  { key: 'approved', label: 'อนุมัติ' },
  { key: 'rejected', label: 'ไม่อนุมัติ' },
  { key: 'cancelled', label: 'ยกเลิก' },
  { key: '', label: 'ทั้งหมด' },
]

/**
 * พนักงาน › การลา › คำขอลา — the decision queue.
 *
 * Opens on รอพิจารณา, because that is the only state that is work. Everything else
 * is history and is one click away.
 *
 * 🔑 Approving does NOT write a parallel record: it writes real ลากิจ/ลาป่วย rows
 * into employee_timetable, through the same api 290 / api 354 the live Bubble app
 * uses — so the roster, the clock-in report and payroll all see it immediately,
 * and Bubble receives its `timetable_employee_new|` / `timetable_employee_edit|`
 * realtime event. The loop lives in functions/api/hr/leave-request/[id].js; read
 * the header there before changing anything about approval.
 */
export default function EmployeeLeaveRequests() {
  const [status, setStatus] = usePersistedState('emp:lvreq:status', 'pending')
  const [branchId, setBranchId] = usePersistedState('emp:lvreq:branch', '')
  // Shared with every พนักงาน tab — see EmployeeFilter.jsx.
  const [employeeId, setEmployeeId] = useEmployeeFilter()
  const [rows, setRows] = useState([])
  const [employees, setEmployees] = useState([])
  const [branches, setBranches] = useState([])
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [open, setOpen] = useState(null)

  useEffect(() => {
    cachedGet('/api/branches').then((r) => setBranches(Array.isArray(r) ? r : (r?.items || []))).catch(() => {})
    cachedGet('/api/hr/employees', { per_page: 500 })
      .then((r) => setEmployees(Array.isArray(r) ? r : (r?.items || []))).catch(() => {})
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    setErr('')
    try {
      const r = await apiGet('/api/hr/leave-request', status ? { status } : undefined)
      setRows(Array.isArray(r?.items) ? r.items : [])
    } catch (e) {
      setErr(apiErrorText(e, 'โหลดคำขอลาไม่สำเร็จ'))
    } finally {
      setLoading(false)
    }
  }, [status])

  useEffect(() => { load() }, [load])

  const empById = useMemo(() => {
    const m = new Map()
    for (const e of employees) m.set(Number(e.id), e)
    return m
  }, [employees])

  // indexBranches returns a Map of id → NAME STRING (not an object) — .get(), and
  // no .name on the result.
  const branchMap = useMemo(() => indexBranches(branches), [branches])

  const visible = useMemo(() => {
    const b = Number(branchId)
    return rows.filter((r) => {
      if (employeeId && String(r.employee_id) !== String(employeeId)) return false
      if (!b) return true
      const emp = empById.get(Number(r.employee_id))
      return Number(r.branch_id) === b || Number(emp?.branch_id) === b
    })
  }, [rows, branchId, employeeId, empById])

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex items-center gap-1">
          {FILTERS.map((f) => (
            <button
              key={f.key || 'all'}
              type="button"
              onClick={() => setStatus(f.key)}
              className={`px-2.5 py-1.5 rounded-lg text-[13px] transition ${
                status === f.key
                  ? 'bg-brand-50 text-brand-700 ring-1 ring-brand-100 font-medium'
                  : 'text-slate-500 hover:bg-slate-50 hover:text-slate-900'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Filter size={13} className="text-slate-400" />
          <EmployeePicker value={employeeId} onChange={setEmployeeId} allowAll />
          <select value={branchId} onChange={(e) => setBranchId(e.target.value)} className={SELECT}>
            <option value="">ทุกสาขา</option>
            {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </div>
      </div>

      {err && <p className="text-[13px] text-rose-600 mb-3">{err}</p>}

      {loading ? (
        <div className="py-16 text-center text-slate-400"><Loader2 size={20} className="animate-spin inline" /></div>
      ) : visible.length === 0 ? (
        // A failed load is not "nothing here" — the error above already says so.
        err ? null : <div className="py-16 text-center">
          <Inbox size={24} className="mx-auto text-slate-300 mb-2" />
          <p className="text-[13.5px] text-slate-400">
            {employeeId ? 'ไม่มีรายการของพนักงานที่เลือก' : status === 'pending' ? 'ไม่มีคำขอลาที่รอพิจารณา' : 'ไม่มีรายการ'}
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-100">
          <table className="w-full text-[12.5px]">
            <thead>
              <tr className="text-[11px] text-slate-400 bg-slate-50/70 text-left">
                <th className="px-2.5 py-1.5 font-medium">พนักงาน</th>
                <th className="px-2.5 py-1.5 font-medium">ประเภท</th>
                <th className="px-2.5 py-1.5 font-medium">ช่วงวันลา</th>
                <th className="px-2.5 py-1.5 font-medium text-right">วัน</th>
                <th className="px-2.5 py-1.5 font-medium">เหตุผล</th>
                <th className="px-2.5 py-1.5 font-medium">สถานะ</th>
              </tr>
            </thead>
            <tbody>
              {visible.map((r) => {
                const emp = empById.get(Number(r.employee_id))
                const meta = leaveTypeMeta(r.type)
                const needsMeeting = r.type === LEAVE_PERSONAL && Number(r.days) > PERSONAL_REVIEW_DAYS
                const warn = applyWarning(r)
                return (
                  <tr
                    key={r.id}
                    onClick={() => setOpen(r)}
                    className="border-t border-slate-50 hover:bg-brand-50/30 cursor-pointer"
                  >
                    {/* The row opens the REQUEST to decide it; the person cell opens
                        the person — quota questions are usually "who is this?" first. */}
                    <td className="px-2.5 py-2">
                      <EmployeeTrigger employeeId={r.employee_id} className="items-center gap-2">
                        <span className="w-7 h-7 rounded-lg bg-brand-50 text-brand-700 text-[11.5px] font-semibold flex items-center justify-center shrink-0 ring-1 ring-brand-100">
                          {nameInitial(emp?.nickname || emp?.name || '?')}
                        </span>
                        <div className="min-w-0">
                          <div className="font-semibold text-slate-800 truncate">{emp?.code || `#${r.employee_id}`}</div>
                          <div className="text-[11px] text-slate-400 truncate">
                            {emp ? fullName(emp) : ''}
                            {branchMap.get(Number(r.branch_id)) ? ` · ${branchMap.get(Number(r.branch_id))}` : ''}
                          </div>
                        </div>
                      </EmployeeTrigger>
                    </td>
                    <td className="px-2.5 py-2">
                      <span className={`px-2 py-0.5 rounded-lg text-[11.5px] font-medium ring-1 ${meta.tone}`}>{meta.short}</span>
                    </td>
                    <td className="px-2.5 py-2 whitespace-nowrap tabular-nums">{fmtRange(r.date_from, r.date_to)}</td>
                    <td className="px-2.5 py-2 text-right tabular-nums">{r.days}</td>
                    <td className="px-2.5 py-2 max-w-[220px]">
                      <div className="truncate text-slate-600">{r.reason || '—'}</div>
                      {/* Flagged where the decision is made, not only on the form the
                          employee saw. HR asked to meet the person before granting a
                          long ลากิจ, so the queue has to say which ones those are. */}
                      {needsMeeting && r.status === 'pending' && (
                        <div className="text-[11px] text-amber-700 mt-0.5">ต้องประชุมกับฝ่ายบุคคลก่อน</div>
                      )}
                      {warn && <div className="text-[11px] text-amber-700 mt-0.5">{warn}</div>}
                    </td>
                    <td className="px-2.5 py-2">
                      <span className={`px-2 py-0.5 rounded-lg text-[11.5px] font-medium ring-1 whitespace-nowrap ${statusTone(r.status)}`}>
                        {statusLabel(r.status)}
                      </span>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {open && (
        <LeaveRequestDrawer
          request={open}
          employee={empById.get(Number(open.employee_id))}
          branches={branches}
          onClose={() => setOpen(null)}
          onSaved={() => { setOpen(null); load() }}
        />
      )}
    </div>
  )
}
